import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _uniqId from 'lodash/uniqueId';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import { Row, Button, Affix } from 'grab-ui';
import { ExceptMobile } from 'components/v2/responsive';
import {
  getQuoteToRender,
  getQuotesToRender,
  allowToPlaceOrder,
} from 'helper/order';
import {
  getFormattedAmount,
  getFormattedTotalAmountFromQuotes,
  isValidQuote,
} from 'helper/quote';
import { getUserSelectedCityCountryCode } from 'helper/user';
import cx from 'classnames';

import css from './order-summary-widget.less';

class OrderSummaryWidget extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hideMobilePriceBreakdown: true,
    };
  }

  enableSingleOrderButton = order => {
    if (_isEmpty(order)) return false;

    return isValidQuote(getQuoteToRender(order)) && allowToPlaceOrder(order);
  };

  // For bulk orders, only enable order button if all order are qualified
  enableBulkOrderButton = orders =>
    orders.every(order => this.enableSingleOrderButton(order));

  toggleMobilePriceDetails = () => {
    this.setState({
      hideMobilePriceBreakdown: !this.state.hideMobilePriceBreakdown,
    });
  };

  renderMobilePriceInstruction = quotes => {
    const { hideMobilePriceBreakdown } = this.state;
    // TO-DO localization
    if (hideMobilePriceBreakdown) {
      return `Show price for ${quotes.length} delivery`;
    }

    return `Hide price for ${quotes.length} delivery`;
  };

  renderSingleOrderSummary = (onClickPlaceOrder, order, isLoading) => {
    const { userInfo, msdSaving } = this.props;
    const enableBookButton = this.enableSingleOrderButton(order);
    const quote = getQuoteToRender(order);
    const fetchingQuote = _get(quote, 'fetchingQuote');
    const formattedAmount = getFormattedAmount(
      quote,
      getUserSelectedCityCountryCode(userInfo),
    );

    return (
      <div className={css.priceBreakdown}>
        <div>
          <ExceptMobile>
            <h4>{I18n.t('v2.homepage.manualOrder.heading.priceBreakdown')}</h4>
            <Row
              type="flex"
              justify="space-between"
              className={css.aditionalFee}
            >
              <h6>{I18n.t('booking.fieldLabel.serviceFee')}</h6>
              <h6>{fetchingQuote ? '--' : formattedAmount}</h6>
            </Row>
          </ExceptMobile>
          <Row type="flex" justify="space-between" className={css.totalPrice}>
            <h5>{I18n.t('booking.totalPrice')}</h5>
            <h3>{fetchingQuote ? '--' : formattedAmount}</h3>
          </Row>
          {msdSaving}
          <Button
            type="primary"
            className={cx(css.bookingButton, {
              [css.creatingButton]: isLoading,
            })}
            onClick={onClickPlaceOrder}
            disabled={!enableBookButton || isLoading}
            loading={isLoading}
          >
            {I18n.t('v2.homepage.manualOrder.button.bookSingleOrder')}
          </Button>
        </div>
      </div>
    );
  };

  renderMultipleOrdersSummary = (onClickPlaceOrder, orders, isLoading) => {
    const { hideMobilePriceBreakdown } = this.state;
    const { userInfo, msdCta } = this.props;
    const quotes = getQuotesToRender(orders);
    const countryCode = getUserSelectedCityCountryCode(userInfo);
    const totalAmountDisplay = getFormattedTotalAmountFromQuotes(
      quotes,
      countryCode,
    );
    const enableBookButton = this.enableBulkOrderButton(orders);
    const validQuoteAmount = _get(
      quotes.filter(quote => !_isEmpty(quote) && _isEmpty(quote.errors)),
      'length',
    );

    return (
      <Affix offsetTop={24}>
        <div className={css.priceBreakdown}>
          <div>
            <h4>{I18n.t('v2.homepage.manualOrder.heading.priceBreakdown')}</h4>
            {quotes.map((quote, index) => (
              <Row
                key={_uniqId('orderPrice')}
                type="flex"
                justify="space-between"
                className={
                  hideMobilePriceBreakdown
                    ? css.aditionalFee
                    : css.mobileAdditionalFee
                }
              >
                <h6>
                  {I18n.t('v2.homepage.manualOrder.heading.deliveryNo', {
                    no: index + 1,
                  })}
                </h6>
                <h6>{getFormattedAmount(quote, countryCode)}</h6>
              </Row>
            ))}
            <Row
              type="flex"
              justify="space-between"
              className={css.mobileToggle}
              onClick={this.toggleMobilePriceDetails}
            >
              <h5>{this.renderMobilePriceInstruction(quotes)}</h5>
            </Row>
            <Row type="flex" justify="space-between" className={css.totalPrice}>
              <h5>{I18n.t('booking.totalPrice')}</h5>
              <h3>{totalAmountDisplay}</h3>
            </Row>
            {msdCta}
            <Button
              type="primary"
              className={cx(css.bookingButton, {
                [css.creatingButton]: isLoading,
              })}
              onClick={onClickPlaceOrder}
              disabled={!enableBookButton || isLoading}
              loading={isLoading}
            >
              {I18n.t('v2.homepage.manualOrder.button.bookMultipleOrder', {
                count: validQuoteAmount,
                amt: validQuoteAmount,
              })}
            </Button>
          </div>
        </div>
      </Affix>
    );
  };

  render() {
    const { isForBulk, onClickPlaceOrder, isLoading } = this.props;
    if (isForBulk) {
      const { orders } = this.props;
      return this.renderMultipleOrdersSummary(
        onClickPlaceOrder,
        orders,
        isLoading,
      );
    }

    const { order } = this.props;
    return this.renderSingleOrderSummary(onClickPlaceOrder, order, isLoading);
  }
}

OrderSummaryWidget.propTypes = {
  isForBulk: PropTypes.bool.isRequired,
  order: PropTypes.shape({}),
  orders: PropTypes.arrayOf(PropTypes.shape({})),
  onClickPlaceOrder: PropTypes.func.isRequired,
  userInfo: PropTypes.shape({}).isRequired,
  msdSaving: PropTypes.elementType,
  msdCta: PropTypes.elementType,
  isLoading: PropTypes.bool,
};

OrderSummaryWidget.defaultProps = {
  isForBulk: false,
  order: {},
  orders: [],
  msdSaving: null,
  msdCta: null,
  isLoading: false,
};

export default OrderSummaryWidget;
